import { createContext, useContext, useMemo, useState } from 'react'

const STORAGE_KEY = 'm1nt-auth-session'
const AuthSessionContext = createContext(null)

function readStoredSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.user) return null
    return parsed
  } catch {
    return null
  }
}

/**
 * @param {object | null} payload
 * — `null`: logout
 * — `{ user, emailAuthToken?, onboardingToken? }`: login / onboarding / completion
 * — bare user `{ id, ... }`: merges tokens from previous session (profile picture / profile save events)
 */
export function AuthSessionProvider({ children }) {
  const [session, setSession] = useState(() => readStoredSession())

  const setAuthSession = (payload) => {
    if (!payload) {
      setSession(null)
      try {
        localStorage.removeItem(STORAGE_KEY)
      } catch {
        // ignore
      }
      return
    }

    const prev = readStoredSession() || {}

    let user
    let emailAuthToken
    let onboardingToken

    if (payload.user && typeof payload.user === 'object') {
      user = payload.user
      emailAuthToken =
        payload.emailAuthToken !== undefined
          ? payload.emailAuthToken
          : (prev.emailAuthToken ?? null)
      onboardingToken =
        payload.onboardingToken !== undefined
          ? payload.onboardingToken
          : (prev.onboardingToken ?? null)
    } else if (payload.id && typeof payload.id === 'string') {
      user = payload
      emailAuthToken = prev.emailAuthToken ?? null
      onboardingToken = prev.onboardingToken ?? null
    } else {
      return
    }

    const next = {
      user,
      emailAuthToken: emailAuthToken || null,
      onboardingToken: onboardingToken || null,
    }

    setSession(next)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      // ignore storage errors
    }
  }

  const value = useMemo(
    () => ({
      user: session?.user ?? null,
      wallet: session?.user?.wallet ?? null,
      emailAuthToken: session?.emailAuthToken ?? null,
      onboardingToken: session?.onboardingToken ?? null,
      /** True while the server issued an onboarding token (name / wallet / password step). */
      needsEmailOnboarding: Boolean(session?.onboardingToken),
      isAuthenticated: Boolean(session?.user),
      setAuthSession,
      logout: () => setAuthSession(null),
    }),
    [session]
  )

  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>
}

export function useAuthSession() {
  const context = useContext(AuthSessionContext)
  if (!context) throw new Error('useAuthSession must be used within AuthSessionProvider')
  return context
}
